import { DEFAULT_Y_AXIS_OPTIONS } from "entities/charts/options/yAxis";

export const HIGHLIGHTED_WIDTH = 6;
export const DEFAULT_WIDTH = 2;
export const GAP_EDGE_WIDTH = 3;
export const DEFAULT_Z_INDEX = 2;
export const SELECTED_WELL_COLOR = "#000";
//Series Ranking should be:
//selected series > highlighted series > type well series > statistics series == line of best fit series > probit highlighted > default
export const STATISTICS_Z_INDEX = DEFAULT_Z_INDEX + 3;
export const TYPEWELL_Z_INDEX = DEFAULT_Z_INDEX + 4;
export const LINE_OF_BEST_FIT_Z_INDEX = DEFAULT_Z_INDEX + 3;
export const SELECTED_WELL_Z_INDEX = DEFAULT_Z_INDEX + 18;
export const HIGHLIGHTED_Z_INDEX = DEFAULT_Z_INDEX + 8;
export const PROBIT_HIGHLIGHTED_Z_INDEX = DEFAULT_Z_INDEX + 1;
export const BLUR_OPACITY = 0.5;
/**
 * Dim level for de-emphasised scatter/jitter POINTS, as against BLUR_OPACITY (0.5) for
 * lines — dots must fall back further or a dense cloud swallows the emphasised group.
 */
export const POINT_BLUR_OPACITY = 0.2;
export const WELL_COUNT_SERIES_COLOR = "#121212";
export const WELL_COUNT_SERIES_WIDTH = 3;

export const WELL_COUNT_LABEL = "Well Count";
export const FACILITY_COUNT_LABEL = "Facility Count";

/**
 * Reserved groupTitle the backend stamps on the single SUMMED count series — the one
 * drawn on the chart's secondary axis. Per-product count series carry their product name
 * as groupTitle and are consumed only by the data table. Identifying the summed series
 * positively (groupTitle === this) instead of by the absence of a groupTitle keeps the
 * intent explicit and verifiable.
 *
 * MUST match TotalRateDateChart.SummedCountGroupTitle in eva-chart-svc.
 */
export const COUNT_TOTAL_GROUP = "Aggregate";

/** Returns true for both the well-count and facility-count secondary-axis series. */
export const isCountSeries = (label: string): boolean => {
  const lower = label.toLowerCase();
  return lower === "well count" || lower === "facility count";
};

/**
 * Whether a series should appear on the chart legend / axis. Keeps every non-count
 * series plus only the SUMMED count (groupTitle === COUNT_TOTAL_GROUP); per-product
 * count series (groupTitle set to a product) are data-table only and are dropped.
 * Shared by both legend implementations so they can't drift.
 */
export const isLegendVisibleSeries = (label?: string, groupTitle?: string): boolean =>
  !isCountSeries(label ?? "") || groupTitle === COUNT_TOTAL_GROUP;

/**
 * Whether a series is drawn on the total-rate-date CHART (secondary axis), given the
 * `showWellCount` toggle. Keeps every non-count series; keeps the SUMMED count series
 * (groupTitle === COUNT_TOTAL_GROUP) only while the well-count line is enabled. Per-product
 * count series (product groupTitle) are data-table only and are always dropped.
 *
 * MUST be the single source of truth for the render translator
 * (translators/totalRateDate.ts) and the highlight translator
 * (highlights/series/totalRateDate.ts): the series id set each produces is derived from the
 * post-filter index, so if these two filters diverge the highlight ids stop matching the
 * rendered ids and the highlight update is silently dropped — the EVA-6819 regression. Sharing
 * one predicate makes that divergence impossible.
 */
export const isChartVisibleSeries = (
  label?: string,
  groupTitle?: string,
  showWellCount?: boolean
): boolean =>
  !isCountSeries(label ?? "") || (!!showWellCount && groupTitle === COUNT_TOTAL_GROUP);

export const UNFOCUSED_FORECAST_OPACITY = 0.5;

export const TOTAL_RATE_DATE_PRIMARY_Y_LOCK_POSITION = {
  min: { bottom: 59, left: 18 },
  max: { top: 66, left: 18 }
};

// max.right is the count-on anchor, hand-tuned to sit beside the count axis
// and clear of the Well Count toggle. applySpacingToChartOptions pulls it
// toward the plot edge when the count axis is hidden, but never past this.
export const TOTAL_RATE_DATE_X_AXIS_LOCK_POSITION = {
  min: { bottom: 6, left: 61 },
  max: { bottom: 6, right: 77 }
};

export const TOTAL_RATE_DATE_WELL_COUNT_LOCK_POSITION = {
  min: { bottom: 59, right: 30 },
  max: { top: 66, right: 30 }
};

// Statistical series labels
export const AVERAGE_LABEL = "Average";
export const P10_LABEL = "P10";
export const P50_LABEL = "P50";
export const P90_LABEL = "P90";
export const LINE_OF_BEST_FIT_LABEL = "Line of Best Fit";
export const FORECAST_SUFFIX = " Forecast";

export const STATISTIC_SERIES_SET = new Set<string>([
  P10_LABEL,
  P50_LABEL,
  P90_LABEL,
  AVERAGE_LABEL
]);

/**
 * Axis TITLE colour, shared by every chart that draws one.
 *
 * Lives here rather than in a specific chart's translator: it is not rig/frac's
 * or well status's, and having one chart import a styling constant out of a
 * sibling chart's file made the ownership read backwards. The canonical source is
 * the wells charts' axis options (crossPlot / pie / probit), which set this same
 * value — the lean charts were setting only `fontSize`, leaving their titles on
 * ECharts' lighter default so they read visibly greyer on the same dashboard.
 */
export const AXIS_TITLE_COLOR = "#041C2C";

/**
 * Axis TICK-LABEL colour, matching the wells charts'
 * `DEFAULT_[XY]_AXIS_OPTIONS.axisLabel.color`. Lighter than the title above it —
 * titles are darker than the ticks beneath them on every chart in the platform.
 */
export const AXIS_LABEL_COLOR = "#5B6770";

/**
 * Axis line for a VALUE axis — the platform default, re-exported for translators.
 *
 * Needed explicitly because ECharts draws `axisLine` by default on a CATEGORY
 * axis but not on a value one. The wells and rig/frac charts never hit this: they
 * build their axes through `createEntityState(ChartOption.YAxis, …)`, which
 * injects the default. A translator that assembles a plain axis object inherits
 * nothing, so its value axis silently comes out with no line while the category
 * axis beside it has one.
 *
 * Aliased rather than copied. A literal here would read identically today and
 * then drift the moment the platform default is restyled — silently, since the
 * translator tests assert against THIS constant and would stay green while these
 * two charts diverged from every other chart in the app.
 *
 * A frozen COPY, deliberately — not a frozen alias.
 *
 * `getChartScreenshotOptions` documents this repo being bitten by the alias
 * shape for `splitLine`: an axis took the module default by reference, had `show`
 * written onto it, and contaminated every later axis in the session plus the
 * persisted options JSON. Freezing guards against that. But freezing the shared
 * object IN PLACE arms the guard app-wide — `DEFAULT_Y_AXIS_OPTIONS.axisLine` is
 * spread by reference into boxPlot, stackedBar, injectorTotal, crossPlot,
 * trendDateMidstream and `options/chart.ts`, so the next writer on any of those
 * would throw in a user's session rather than in a test.
 *
 * Copying keeps everything the alias was for and gives up nothing: the copy is
 * taken at import, so a restyle of the platform default still flows into both
 * well-status charts (that drift protection was the whole point), the translator
 * tests compare with `toEqual` so they stay green, and a stray write poisons this
 * constant alone instead of every chart that spreads the default.
 */
export const VALUE_AXIS_LINE = Object.freeze({
  ...DEFAULT_Y_AXIS_OPTIONS.axisLine,
  lineStyle: { ...DEFAULT_Y_AXIS_OPTIONS.axisLine?.lineStyle }
});
